/**
 * secure-score-brief.ts — Editorial copy layer for the Microsoft Secure Score
 * page standfirst. Mirrors `posture-brief.ts` in shape, seeding, and voice.
 *
 * The Secure Score story keys off three signals: percentage of max, 30-day
 * delta, and weakest of the four Microsoft categories (Identity / Data /
 * Apps / Device). Verdicts ≤6 words.
 *
 * Loading state: when `score` is null/undefined we return a "Reading score…"
 * brief rather than synthesizing a worst-band verdict.
 */

import type { SecureScoreResult } from '@/types/secureScore'

/* -------------------------------------------------------------------------- */
/* Public shape                                                               */
/* -------------------------------------------------------------------------- */

export interface SecureScoreBrief {
  kicker: string
  headlineSubject: string
  headlineModifier: string
  weakLink: string
  deck: string
  verdict: string
  byline: string
  delta: { value: number; days: number }
}

export type SecureScoreCategoryKey = 'identity' | 'data' | 'apps' | 'device'

const CATEGORY_LABEL: Record<SecureScoreCategoryKey, string> = {
  identity: 'Identity',
  data: 'Data',
  apps: 'Apps',
  device: 'Device',
}

export interface SecureScoreCategorySummary {
  identity: number
  data: number
  apps: number
  device: number
}

export interface SecureScoreTopRec {
  title: string
  gap: number
  category: string
}

export interface SecureScoreBriefInput {
  userEmail?: string
  tenantId?: string
  /** Microsoft Secure Score result. When null we return the loading brief. */
  score?: SecureScoreResult | null
  /** Top open recommendations sorted by gap desc. May be empty. */
  topRecs?: SecureScoreTopRec[]
  /** Per-category percentages (0-100). */
  categories?: SecureScoreCategorySummary
  /** Total points-to-gain across all open recs. */
  totalPtsToGain?: number
  /** 30-day score delta in points. Null when history is too short. */
  delta30d?: number | null
  asOfDate?: Date
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function formatKicker(now: Date): string {
  const fmt = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long' })
  return `Today's Brief · ${fmt.format(now)}`
}

function localDayKey(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function hash(s: string): number {
  let h = 5381
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h) ^ s.charCodeAt(i)
  }
  return h >>> 0
}

function pickVariant<T>(variants: readonly T[], seed: string, slot: string): T {
  if (variants.length === 0) throw new Error(`pickVariant: empty variants for slot "${slot}"`)
  const idx = hash(`${seed}|${slot}`) % variants.length
  return variants[idx]
}

function weakestCategory(
  cats: SecureScoreCategorySummary | undefined,
): SecureScoreCategoryKey | undefined {
  if (!cats) return undefined
  const keys: SecureScoreCategoryKey[] = ['identity', 'data', 'apps', 'device']
  let best: { key: SecureScoreCategoryKey; pct: number } | undefined
  for (const k of keys) {
    const v = cats[k]
    if (typeof v !== 'number') continue
    if (!best || v < best.pct) best = { key: k, pct: v }
  }
  return best?.key
}

/* -------------------------------------------------------------------------- */
/* Verdict variants                                                           */
/* -------------------------------------------------------------------------- */

const VERDICT_CLIMBING_FAST: readonly string[] = [
  'Real lift. Keep the cadence.',
  'On the up. Keep going.',
  'Big jump. Keep it going.',
]

const VERDICT_HOLDING_TOP: readonly string[] = [
  'Holding steady at the top.',
  'Top form. Just keep watch.',
  'Posture is settled.',
]

/** Templates take an `{n}` token (delta in points, integer). */
const VERDICT_CLIMBING: readonly string[] = [
  'Up {n} pts. Nice progress.',
  'Up {n} pts this month.',
  'Up {n} pts. Keep at it.',
]

const VERDICT_SLIPPED: readonly string[] = [
  'Small dip. Worth a look.',
  'Soft week. Easy to recover.',
  "Slipped a touch. Easy to recover.",
]

/** Templates take a `{cat}` token (single-word category label). */
const VERDICT_LAGS: readonly string[] = [
  '{cat} first. Biggest move today.',
  'Start with {cat}. Biggest move.',
  '{cat} is the soft spot.',
]

const VERDICT_LAGS_NO_TARGET: readonly string[] = [
  'Soft floor. Spread the work.',
  'Behind the bar. Pick anywhere.',
  'Score is soft. Take it broadly.',
]

function pickVerdict(
  pct: number,
  delta30d: number | null | undefined,
  weakest: SecureScoreCategoryKey | undefined,
  seed: string,
): string {
  const delta = typeof delta30d === 'number' ? delta30d : 0

  if (delta >= 10) return pickVariant(VERDICT_CLIMBING_FAST, seed, 'verdict.climbingFast')

  if (delta >= 0 && pct >= 75) {
    return pickVariant(VERDICT_HOLDING_TOP, seed, 'verdict.holdingTop')
  }

  if (delta > 0 && pct >= 50) {
    const tpl = pickVariant(VERDICT_CLIMBING, seed, 'verdict.climbing')
    return tpl.replace('{n}', String(Math.round(delta)))
  }

  if (delta < 0 && pct >= 60) {
    return pickVariant(VERDICT_SLIPPED, seed, 'verdict.slipped')
  }

  if (weakest) {
    const tpl = pickVariant(VERDICT_LAGS, seed, 'verdict.lags')
    return tpl.replace('{cat}', CATEGORY_LABEL[weakest])
  }
  return pickVariant(VERDICT_LAGS_NO_TARGET, seed, 'verdict.lags.noTarget')
}

/* -------------------------------------------------------------------------- */
/* Modifier + weak-link                                                       */
/* -------------------------------------------------------------------------- */

function pickModifier(pct: number, delta: number): string {
  if (pct >= 85) return delta > 0 ? 'lifting' : 'holding'
  if (pct >= 70) return delta > 0 ? 'strong' : 'mixed'
  if (pct >= 55) return 'weakening'
  return 'fragile'
}

const WEAKLINK_TIGHTEN: readonly string[] = [
  'Room to tighten things up.',
  'Just the rough edges left.',
  'Quiet hygiene work to round it out.',
]

/** Templates take a `{cat}` token. */
const WEAKLINK_LAGGARD: readonly string[] = [
  '{cat} is the soft spot.',
  '{cat} is the one quietly dragging.',
  '{cat} is dragging the score down today.',
]

const WEAKLINK_DRIFT: readonly string[] = [
  'The score is drifting the wrong way.',
  'Score is heading the wrong way today.',
]

function buildWeakLink(
  pct: number,
  delta: number,
  weakest: SecureScoreCategoryKey | undefined,
  seed: string,
): string {
  if (delta < -3) return pickVariant(WEAKLINK_DRIFT, seed, 'weakLink.drift')
  if (weakest && (pct < 70 || delta < 0)) {
    const tpl = pickVariant(WEAKLINK_LAGGARD, seed, 'weakLink.laggard')
    return tpl.replace('{cat}', CATEGORY_LABEL[weakest])
  }
  return pickVariant(WEAKLINK_TIGHTEN, seed, 'weakLink.tighten')
}

/* -------------------------------------------------------------------------- */
/* Deck                                                                       */
/* -------------------------------------------------------------------------- */

const TREND_LIFTING: readonly string[] = [
  'Microsoft Secure Score has lifted nicely over the past 30 days.',
  'The score is up over the past month, with progress across categories.',
  'Modest gains across the past 30 days, spread evenly across the four categories.',
]

const TREND_FLAT: readonly string[] = [
  'The score has held flat over the past 30 days. Not gaining ground, not losing it either.',
  "The score hasn't moved over the past month. Same story as last month.",
  'Steady-state for 30 days, with nothing meaningful shifting either way.',
]

const TREND_SLIPPING: readonly string[] = [
  'The score has slipped slightly over the past 30 days. Worth a look when you can.',
  'Modest decline over the past month. Worth a look when you can.',
  'Small dip across the past 30 days. Not yet a trend, but keep an eye on it.',
]

const TREND_FALLING: readonly string[] = [
  'The score has fallen meaningfully over the past 30 days. Needs eyes on it this week.',
  'Sharp decline across the past month. This needs a real response, not next sprint.',
]

/** Templates take `{phrase}` (already-bolded rec title). */
const RISK_TOP_REC: readonly string[] = [
  '{phrase} is your biggest move on the board today.',
  '{phrase} is where the points pay back fastest.',
  '{phrase} is where the most points are sitting unclaimed.',
]

/** Templates take `{cat}` (already-bolded category label). */
const RISK_CATEGORY: readonly string[] = [
  '{cat} is the lagging category. Lift it to lift the score.',
  '{cat} is the one quietly dragging the score today.',
  "{cat} is dragging the score down today; it's the place to start.",
]

const RISK_HYGIENE: readonly string[] = [
  "Nothing dominates the picture. It's housekeeping from here.",
  'The remaining items are hygiene rather than headline risks.',
]

const RISK_COMPOUND: readonly string[] = [
  'Several smaller gaps add up; closing the biggest of them lifts the score fastest.',
  'A handful of smaller gaps stack up. The biggest of them is the place to start.',
]

const ACTION_HOLD: readonly string[] = [
  "Keep doing what you're doing; the score is doing its job.",
  "You're on the right track. Keep the routine reviews running.",
]

const ACTION_LEAD: readonly string[] = [
  'Knock out the lead recommendation this week to keep the lift going.',
  'Take the top recommendation this week.',
]

const ACTION_TRIAGE: readonly string[] = [
  'Take the biggest items first; the rest can wait until next sprint.',
  'Focus on the points that pay back fastest; the rest holds for next sprint.',
]

const ACTION_PUSH: readonly string[] = [
  'This one needs a real week of focus, not routine work.',
  'Plan a focused push this sprint, not routine work.',
]

function buildDeck(
  pct: number,
  delta: number,
  weakest: SecureScoreCategoryKey | undefined,
  topRec: SecureScoreTopRec | undefined,
  seed: string,
): string {
  const trend = (() => {
    if (delta >= 3) return pickVariant(TREND_LIFTING, seed, 'deck.trend.lifting')
    if (delta > 0) return pickVariant(TREND_LIFTING, seed, 'deck.trend.lifting')
    if (delta === 0) return pickVariant(TREND_FLAT, seed, 'deck.trend.flat')
    if (delta >= -3) return pickVariant(TREND_SLIPPING, seed, 'deck.trend.slipping')
    return pickVariant(TREND_FALLING, seed, 'deck.trend.falling')
  })()

  const risk = (() => {
    if (topRec?.title) {
      const phrase = `**${topRec.title.replace(/[.,;:!?]+$/, '')}**`
      const tpl = pickVariant(RISK_TOP_REC, seed, 'deck.risk.topRec')
      return tpl.replace('{phrase}', phrase)
    }
    if (weakest && pct < 70) {
      const cat = `**${CATEGORY_LABEL[weakest]}**`
      const tpl = pickVariant(RISK_CATEGORY, seed, 'deck.risk.category')
      return tpl.replace('{cat}', cat)
    }
    if (pct >= 85) return pickVariant(RISK_HYGIENE, seed, 'deck.risk.hygiene')
    return pickVariant(RISK_COMPOUND, seed, 'deck.risk.compound')
  })()

  const action = (() => {
    if (pct >= 90) return pickVariant(ACTION_HOLD, seed, 'deck.action.hold')
    if (pct >= 75) return pickVariant(ACTION_LEAD, seed, 'deck.action.lead')
    if (pct >= 60) return pickVariant(ACTION_TRIAGE, seed, 'deck.action.triage')
    return pickVariant(ACTION_PUSH, seed, 'deck.action.push')
  })()

  return `${trend} ${risk} ${action}`
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

export function composeSecureScoreBrief(
  input: SecureScoreBriefInput = {},
): SecureScoreBrief {
  const userEmail = input.userEmail ?? 'you'
  const now = input.asOfDate ?? new Date()
  const delta = typeof input.delta30d === 'number' ? input.delta30d : 0

  const byline =
    userEmail === 'you'
      ? 'Brief by **Guardian Continuous Assessment**'
      : `Brief by **Guardian Continuous Assessment** · For **${userEmail}**`

  if (input.score == null) {
    return {
      kicker: formatKicker(now),
      headlineSubject: 'Microsoft Secure Score is',
      headlineModifier: 'loading',
      weakLink: 'Reading the latest signal.',
      deck: 'Pulling the latest Secure Score data. The brief updates once Microsoft responds.',
      verdict: 'Reading score…',
      byline,
      delta: { value: delta, days: 30 },
    }
  }

  const pct = input.score.percentage
  const seed = `${input.tenantId ?? 'guardian'}:${localDayKey(now)}`
  const cats = input.categories
  const weakest = weakestCategory(cats)
  const topRec = (input.topRecs ?? [])[0]

  return {
    kicker: formatKicker(now),
    headlineSubject: 'Secure Score is',
    headlineModifier: pickModifier(pct, delta),
    weakLink: buildWeakLink(pct, delta, weakest, seed),
    deck: buildDeck(pct, delta, weakest, topRec, seed),
    verdict: pickVerdict(pct, delta, weakest, seed),
    byline,
    delta: { value: delta, days: 30 },
  }
}

/* -------------------------------------------------------------------------- */
/* Test-only exports                                                          */
/* -------------------------------------------------------------------------- */

export const __TEST_ONLY__ = {
  VERDICT_CLIMBING_FAST,
  VERDICT_HOLDING_TOP,
  VERDICT_CLIMBING,
  VERDICT_SLIPPED,
  VERDICT_LAGS,
  VERDICT_LAGS_NO_TARGET,
  WEAKLINK_TIGHTEN,
  WEAKLINK_LAGGARD,
  WEAKLINK_DRIFT,
  RISK_TOP_REC,
  RISK_CATEGORY,
  RISK_HYGIENE,
  RISK_COMPOUND,
  CATEGORY_LABEL,
}
